import { __rest } from 'tslib';
import React from 'react';
import { extractMarginProps, extractPaddingProps } from '@10d/tend-ui-styling';
import { useTranslation } from '@10d/tend-ui-locale';
import { isString } from '@10d/tend-ui-utils';
import { useForwardRef } from '@10d/tend-ui-hooks';
import cn from 'classnames';
import { Close } from '@10d/tend-ui-icons/Close';
import { useTheme, useColor } from '@10d/tend-ui-theme';
import { Root } from './styled.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import styles from './Tag.module.css.js';

const BaseTag = function (_a, ref) {
    var { ellipsis = false, width, maxWidth, closable = false, shape = 'ellipse', size = 'medium', preset = 'default', color, backgroundColor, children, onClose, closeIconTooltip, before, after, borderRadius, className, style } = _a, props = __rest(_a, ["ellipsis", "width", "maxWidth", "closable", "shape", "size", "preset", "color", "backgroundColor", "children", "onClose", "closeIconTooltip", "before", "after", "borderRadius", "className", "style"]);
    const theme = useTheme();
    const t = useTranslation();
    const [visible, setVisible] = React.useState(true);
    const _color = useColor(color);
    // FIXME: Find a workaround
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const _backgroundColor = useColor(backgroundColor);
    const handleClose = React.useCallback((e) => {
        setVisible(false);
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
    }, [onClose]);
    const tooltipProps = React.useMemo(() => {
        if (!closeIconTooltip)
            return { title: t(['general', 'close']) };
        return closeIconTooltip;
    }, [closeIconTooltip, t]);
    const _ref = useForwardRef(ref);
    const [_width, _setWidth] = React.useState(null);
    React.useEffect(() => {
        if (!_ref.current)
            return;
        const width = _ref.current.getBoundingClientRect().width;
        if (!width)
            return;
        _setWidth(width);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const _style = React.useMemo(() => {
        if (!_width)
            return Object.assign(Object.assign({}, style), { whiteSpace: 'nowrap' });
        if (ellipsis)
            return Object.assign(Object.assign({}, style), { display: 'inline-block', whiteSpace: 'nowrap', width: _width, textOverflow: 'ellipsis', overflow: 'hidden' });
        return Object.assign(Object.assign({}, style), { whiteSpace: 'nowrap' });
    }, [_width, ellipsis, style]);
    const _b = React.useMemo(() => extractMarginProps(props), [props]), { rest: withoutMargins } = _b, margins = __rest(_b, ["rest"]);
    const _c = React.useMemo(() => extractPaddingProps(withoutMargins), [withoutMargins]), { rest } = _c, paddings = __rest(_c, ["rest"]);
    if (!visible)
        return null;
    const title = ellipsis ? (isString(children) ? children : undefined) : undefined;
    return (React.createElement(Root, Object.assign({ "data-testid": 'tend-ui-tag', title: title }, rest, margins, paddings, { ref: _ref, theme: theme, "$color": _color, "$backgroundColor": _backgroundColor, "$preset": preset, "$pointer": !!props.onClick, "$borderRadius": borderRadius, "$width": width, "$maxWidth": maxWidth, "$size": size, "$shape": shape, className: cn(['tend-ui-tag', className], {
            /**
             * Presets
             */
            [styles['tend-ui-tag-preset-default']]: preset === 'default',
            [styles['tend-ui-tag-preset-gray']]: preset === 'gray',
            [styles['tend-ui-tag-preset-blue']]: preset === 'blue',
            [styles['tend-ui-tag-preset-geekblue']]: preset === 'geekblue',
            [styles['tend-ui-tag-preset-green']]: preset === 'green',
            [styles['tend-ui-tag-preset-yellow']]: preset === 'yellow',
            [styles['tend-ui-tag-preset-red']]: preset === 'red',
            [styles['tend-ui-tag-preset-cyan']]: preset === 'cyan',
            [styles['tend-ui-tag-preset-volcano']]: preset === 'volcano',
            [styles['tend-ui-tag-preset-purple']]: preset === 'purple',
            [styles['tend-ui-tag-preset-gray-light']]: preset === 'gray-light',
            [styles['tend-ui-tag-preset-blue-light']]: preset === 'blue-light',
            [styles['tend-ui-tag-preset-geekblue-light']]: preset === 'geekblue-light',
            [styles['tend-ui-tag-preset-green-light']]: preset === 'green-light',
            [styles['tend-ui-tag-preset-yellow-light']]: preset === 'yellow-light',
            [styles['tend-ui-tag-preset-red-light']]: preset === 'red-light',
            [styles['tend-ui-tag-preset-cyan-light']]: preset === 'cyan-light',
            [styles['tend-ui-tag-preset-volcano-light']]: preset === 'volcano-light',
            [styles['tend-ui-tag-preset-purple-light']]: preset === 'purple-light',
            /**
             * Shape
             */
            [styles['tend-ui-tag-shape-ellipse']]: shape === 'ellipse',
            [styles['tend-ui-tag-shape-round']]: shape === 'round',
            /**
             * Size
             */
            [styles['tend-ui-tag-size-medium']]: size === 'medium',
            [styles['tend-ui-tag-size-large']]: size === 'large',
        }), style: _style }),
        before,
        children,
        after,
        closable && (React.createElement(Tooltip, Object.assign({}, tooltipProps),
            React.createElement(Close, { onClick: handleClose, size: 12 })))));
};
const Tag = React.forwardRef(BaseTag);
Tag.displayName = 'Tag';

export { Tag };
